import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EyeIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
import { useCurrencyByIP } from '../../hooks/useCurrencyByIP';
// --- Mini View Counter Component ---
function MiniViewCounter() {
  const [count, setCount] = useState(0);
  const [progress, setProgress] = useState(0);
  const [cycle, setCycle] = useState(0);
  const target = 1500000;
  const duration = 2500;
  const pauseBetween = 800;
  useEffect(() => {
    let startTime: number | null = null;
    let rafId: number;
    let pauseTimer: ReturnType<typeof setTimeout>;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const rawProgress = Math.min((timestamp - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - rawProgress, 4);
      setCount(Math.floor(ease * target));
      setProgress(rawProgress);
      if (rawProgress < 1) {
        rafId = requestAnimationFrame(animate);
      } else {
        pauseTimer = setTimeout(() => {
          setCount(0);
          setProgress(0);
          setCycle(c => c + 1);
        }, pauseBetween);
      }
    };
    rafId = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(pauseTimer);
    };
  }, [cycle]);
  const heat = Math.max(0, Math.min(1, (progress - 0.4) / 0.6));
  const numberColor = heat > 0.7 ? '#FF4500' : heat > 0.3 ? '#FF8C00' : '#ffffff';
  const textShadow = heat > 0.3 ? `0 0 ${10 + heat * 20}px rgba(255, 107, 0, ${heat * 0.6})` : 'none';
  return <div className="flex items-center gap-3 md:gap-4" data-id="element-592">
      <div className="relative flex-shrink-0" data-id="element-593">
        <div className="absolute inset-0 rounded-full bg-accent/30 blur-md md:blur-lg" style={{
        transform: `scale(${1 + heat})`,
        opacity: 0.4 + heat * 0.4
      }} data-id="element-594" />
        <EyeIcon className="relative w-6 h-6 md:w-8 md:h-8" style={{
        color: heat > 0.5 ? '#FF4500' : '#FF6B00'
      }} data-id="element-595" />
      </div>
      <div className="flex flex-col leading-none" data-id="element-596">
        <span className="text-xl md:text-3xl font-extrabold tabular-nums tracking-tight" style={{
        color: numberColor,
        textShadow
      }} data-id="element-597">
          {count.toLocaleString('en-US')}
        </span>
        <span className="text-[9px] md:text-xs font-bold uppercase tracking-widest text-accent" data-id="element-598">
          Visualizaciones
        </span>
      </div>
    </div>;
}
// --- Main Sticky Footer ---
export function StickyFooter() {
  const [isVisible, setIsVisible] = useState(false);
  const {
    price,
    originalPrice
  } = useCurrencyByIP();
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);
  return <AnimatePresence data-id="element-599">
      {isVisible && <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 100,
      opacity: 0
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 20
    }} className="fixed bottom-0 left-0 right-0 z-50 px-3 pb-3 pt-1 md:px-4 md:pb-4 md:pt-2" data-id="element-600">
          <div className="max-w-4xl mx-auto" data-id="element-601">
            <div className="bg-surface/98 md:bg-surface/95 mobile-no-blur md:backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/50 rounded-2xl md:rounded-t-2xl md:rounded-b-none p-3 md:px-8 md:py-5 flex items-center justify-between gap-3 md:gap-8" data-id="element-602">
              {/* Left Side: Old price + Counter on desktop */}
              <div className="hidden md:flex items-center gap-4" data-id="element-603">
                <MiniViewCounter data-id="element-604" />
              </div>

              {/* Right Side: Price comparison + CTA */}
              <div className="flex items-center gap-3 w-full md:w-auto" data-id="element-605">
                <div className="flex flex-col items-end flex-shrink-0" data-id="element-606">
                  <span className="text-xs text-neutral-500 line-through" data-id="element-607">
                    {originalPrice}
                  </span>
                  <span className="text-sm font-bold text-accent" data-id="element-608">{price}</span>
                </div>
                <CTAButton className="w-full md:w-auto py-3 px-5 md:px-6 text-sm md:text-lg shadow-lg shadow-accent/20 !m-0" data-id="element-609">
                  Descargar ahora
                </CTAButton>
              </div>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}